import {BaseApi} from './BaseApi'

/**
 * Batch Status Enum (from backend)
 *
 * UPDATED REQUIREMENT (Jan 2025): Status flow clarified
 * NEW FLOW (per Joseph's requirements):
 * - Draft → InProgress → Finalized (batch creation/editing)
 * - After invoice generation: Open (pending customer approval)
 * - After customer approval: Approved (ready for payroll processing)
 * - After Process Payroll clicked: Closed (sent to payroll software)
 *
 * OLD FLOW: Draft → InProgress → Finalized → Approved
 * The enum values remain the same, but interpretation changes:
 * - "Finalized" = batch ready, invoice auto-generated (customer sees as "Open")
 * - "Approved" = customer approved invoice (Platform Admin sees as "Approved")
 * - "Archived/Closed" = sent to payroll software (Platform Admin sees as "Closed")
 */
export enum BatchStatus {
  Draft = 'Draft',
  InProgress = 'InProgress',
  Finalized = 'Finalized',
  Approved = 'Approved',
  Invoiced = 'Invoiced', // ADDED: After invoice is generated from batch
  PaystubsApproved = 'PaystubsApproved', // ADDED: After all paystubs are approved
  PayrollSent = 'PayrollSent', // ADDED: After sent to payroll microservice
  Completed = 'Completed', // ADDED: Payroll fully processed
  Voided = 'Voided', // ADDED: Batch was voided/cancelled
  // Legacy/UI statuses (may be removed in future):
  Rejected = 'Rejected',
  Archived = 'Archived',
  Closed = 'Closed',
}

/**
 * Invoice Status Enum (from backend)
 */
export enum InvoiceStatus {
  Draft = 'Draft',
  Sent = 'Sent',
  Paid = 'Paid',
  Overdue = 'Overdue',
  Cancelled = 'Cancelled',
}

/**
 * EOR Item for EOR List Response
 */
export interface EORItem {
  id: string
  name: string
  employeeCount: number
  activeCompanies: number
  lastPayrollDate: string | null
}

/**
 * EOR List Response
 */
export interface EORListResponse {
  eors: EORItem[]
}

/**
 * Client Item for Client List Response
 */
export interface ClientItem {
  id: string
  name: string
  location: string
  employeeCount: number
  sitesCount: number
  lastPayrollDate: string | null
  status: string
  lastActivity: string
}

/**
 * Client List Response
 */
export interface ClientListResponse {
  clients: ClientItem[]
}

/**
 * Create Batch Request
 * UPDATED: Field names match mspplatform backend
 */
export interface CreateBatchRequest {
  agencyId: string
  companyId: string
  paymentPeriodStart: Date | string
  paymentPeriodEnd: Date | string
  payDate: Date | string
  batchName: string
  status?: BatchStatus
  description?: string
}

/**
 * Update Batch Request
 * UPDATED: Field names match mspplatform backend
 */
export interface UpdateBatchRequest {
  id: string
  agencyId: string
  companyId: string
  batchName?: string
  paymentPeriodStart?: Date | string
  paymentPeriodEnd?: Date | string
  payDate?: Date | string
  status?: BatchStatus
  description?: string
}

/**
 * Batch Response
 * UPDATED: Matches mspplatform backend structure
 * - Removed: batchId (use id instead), employeeCount, totalGross, totalNet, totalDeductions, totalTaxes
 * - Renamed: payPeriodStartDate → paymentPeriodStart, payPeriodEndDate → paymentPeriodEnd, notes → description
 * - Added: batchNumber, externalBatchId, statusChangedAt, statusChangedBy
 */
export interface BatchResponse {
  id: string
  batchName: string
  batchNumber: string
  agencyId: string
  companyId: string
  paymentPeriodStart: Date
  paymentPeriodEnd: Date
  payDate: Date
  status: BatchStatus
  statusChangedAt?: Date
  statusChangedBy?: string
  description?: string
  externalBatchId?: string
  createdDate: Date
  modifiedDate: Date
  createdBy: string
  // Populated fields (not in backend, added at runtime for convenience)
  agencyName?: string
  companyName?: string
}

/**
 * Send to Payroll Response
 * Returned from POST /payroll/batches/:batchId/send-to-payroll
 */
export interface SendToPayrollResponse {
  success: boolean
  message: string
  data: {
    batchId: string
    externalBatchId: string
    status: string
    paystubsProcessed: number
    invoiceItemsCreated: number
    sentAt: string
  }
}

/**
 * Create Batch Earnings Request (for employee earnings records)
 */
export interface CreateBatchEarningsRequest {
  batchId: string
  timecards: TimecardData[]
}

/**
 * Timecard Data for earnings calculation
 */
export interface TimecardData {
  employeeId: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  payRate: number
  overtimeRate?: number
  doubleTimeRate?: number
}

/**
 * Daily Breakdown for timecard (from external app)
 */
export interface DailyTimecardBreakdown {
  date: string
  dayOfWeek: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  totalHours: number
  vacationHours?: number
  sickHours?: number
  bereavementHours?: number
  unpaidHours?: number
  holidayHours?: number
  mealPenalty?: number
  punches?: Array<{
    punchIn: string
    punchOut: string
  }>
}

/**
 * Timecard Response from GET /payroll/timecards (from external app)
 */
export interface TimecardResponse {
  id: string
  versionKey: number
  createdDate: string
  modifiedDate: string
  employeeId: string
  applicantId: string
  assignmentId: string
  weekStartDate: string
  weekEndDate: string
  regularTime: number
  overtime: number
  doubleTime: number
  vacationHours: number
  sickHours: number
  holidayHours: number
  unpaidHours: number
  bereavementHours: number
  dailyBreakdown: DailyTimecardBreakdown[]
  processed: boolean
  receivedDate: string
}

/**
 * Sort fields for paginated timecards
 */
export type TimecardSortField = 'employeeName' | 'totalHours'

/**
 * Parameters for paginated timecards request
 */
export interface TimecardPagingParams {
  pageNumber: number
  pageSize: number
  companyId?: string
  siteId?: string
  departmentId?: string
  jobTitle?: string
  employeeName?: string
  weekStartDate?: string // Format: YYYY-MM-DD
  weekEndDate?: string // Format: YYYY-MM-DD
  sortBy?: TimecardSortField
  sortOrder?: 'asc' | 'desc'
}

/**
 * Individual timecard item in paginated response
 */
export interface TimecardPaginatedItem {
  id: string
  versionKey: number
  createdDate: string
  modifiedDate: string
  employeeId: string
  applicantId: string
  assignmentId: string
  weekStartDate: string
  weekEndDate: string
  regularTime: number
  overtime: number
  doubleTime: number
  vacationHours: number
  sickHours: number
  holidayHours: number
  unpaidHours: number
  bereavementHours: number
  totalHours: number
  processed: boolean
  receivedDate: string
  // Looked-up fields from related collections
  employeeName: string
  firstName: string
  lastName: string
  siteName: string
  siteId: string
  departmentName: string
  departmentId: string
  jobTitle: string
  dailyBreakdown: DailyTimecardBreakdown[]
}

/**
 * Paginated timecards response
 */
export interface TimecardPagingResponse {
  timecards: TimecardPaginatedItem[]
  totalRecords: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

/**
 * Earnings Record Response
 */
export interface EarningsRecordResponse {
  id: string
  batchId: string
  employeeId: string
  employeeName: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  regularPay: number
  overtimePay: number
  doubleTimePay: number
  grossPay: number
  federalTax: number
  stateTax: number
  socialSecurityTax: number
  medicareTax: number
  totalTaxes: number
  deductions: number
  netPay: number
  createdDate: string
}

/**
 * Create Invoice Request (Legacy - kept for compatibility)
 */
export interface CreateInvoiceRequest {
  batchId: string
  dueDate: string
  notes?: string
}

/**
 * Create Blank Invoice Request
 * UPDATED: New endpoint for manual invoice creation
 */
export interface CreateBlankInvoiceRequest {
  companyId: string
  payPeriodStart: Date | string
  payPeriodEnd: Date | string
  agencyId: string
  batchId?: string
}

/**
 * Add Invoice Item Request
 * UPDATED: For adding employees to invoices manually
 */
export interface AddInvoiceItemRequest {
  applicantId: string
  assignmentId: string
  earningsLines: EarningsLine[]
}

/**
 * Update Invoice Item Request
 * UPDATED: For updating employee earnings in invoice
 */
export interface UpdateInvoiceItemRequest {
  earningsLines: EarningsLine[]
}

/**
 * Invoice Generation Summary
 * UPDATED: Summary of auto-generated invoice from batch
 */
export interface InvoiceGenerationSummary {
  totalEmployees: number
  totalTimecards: number
  errors: any[]
}

/**
 * Generate Invoice Response
 * UPDATED: Response from generateInvoiceFromBatch endpoint
 */
export interface GenerateInvoiceResponse {
  invoice: InvoiceResponse
  items: InvoiceItem[]
  summary: InvoiceGenerationSummary
}

/**
 * Earnings Line - represents one paycode type (regular, overtime, etc.) for an employee
 * UPDATED: Matches mspplatform backend InvoiceItem.earningsLines structure
 */
export interface EarningsLine {
  systemCode: string // "regularTime", "overtime", "doubleTime", etc.
  paycodeName: string // "REGULAR", "OVERTIME", "DT"
  paycodeId: string
  payUnit: number // Hours worked
  billUnit: number // Hours billed (same except unpaidHours = 0)
  payRate: number // Base rate or multiplied for OT/DT
  billRate: number // payRate with markup applied
  payAmount: number // payUnit * payRate
  billAmount: number // billUnit * billRate * billablePercentage
  markupPercentage: number
  billablePercentage: number
  nonBillablePercentage: number
}

/**
 * Request DTO for updating an individual earnings line within an invoice item
 * All fields are optional - only provided fields will be updated
 * ADDED: New DTO for PUT /invoices/:invoiceId/items/:itemId/earnings/:paycodeId
 */
export interface UpdateEarningsLineRequest {
  payUnit?: number // Hours worked
  billUnit?: number // Hours billed
  payRate?: number // Pay rate per hour
  billRate?: number // Bill rate per hour
  markupPercentage?: number
  billablePercentage?: number // 0-100
  nonBillablePercentage?: number // 0-100
}

/**
 * Invoice Item - represents ONE EMPLOYEE on an invoice
 * UPDATED: Matches mspplatform backend structure with full details
 * Groups all timecards and earnings for that employee for the pay period
 */
export interface InvoiceItem {
  id: string
  versionKey?: number
  batchId: string
  invoiceId: string
  applicantId: string
  assignmentId: string
  employeeName: string // "lastName, firstName" format
  firstName: string
  lastName: string
  ssn: string // Last 4 digits
  billToName: string // Customer/company name
  departmentName: string
  branch: string // Agency/branch name
  weekWorked: string // "MM/DD/YY - MM/DD/YY"
  weekStartDate?: string // ISO date string
  weekEndDate?: string // ISO date string
  payPeriod: string // "Weekly", "Bi-weekly", etc.
  itemPay: number // Total pay for employee
  itemBill: number // Total bill for employee
  orderType: string // "TEMP", etc.
  poNumber: string
  markupName: string
  markupPct: number // Calculated: itemBill / itemPay
  earningsLines: EarningsLine[] // CRITICAL: Detailed breakdown by paycode
  timecardIds: string[]
  createdDate?: Date
  modifiedDate?: Date
}

/**
 * Invoice Response
 * UPDATED: Matches mspplatform backend structure
 */
export interface InvoiceResponse {
  id: string
  versionKey?: number
  invoiceNumber: string
  batchId: string
  companyId: string
  customerName: string
  agencyId: string
  agencyName: string
  payPeriodStart: Date
  payPeriodEnd: Date
  invoiceDate: Date
  dueDate?: Date
  status: InvoiceStatus
  approvedDate?: Date
  approvedBy?: string
  totalPay: number
  totalBill: number
  subtotal: number
  taxes: number
  total: number
  pdfUrl?: string
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
}

/**
 * Invoice with Items Response
 */
export interface InvoiceWithItemsResponse extends InvoiceResponse {
  items: InvoiceItem[]
}

/**
 * Toast Import Request (for timecard imports from POS systems)
 */
export interface ToastImportRequest {
  batchId: string
  restaurantId: string
  startDate: string
  endDate: string
}

/**
 * Toast Import Response
 */
export interface ToastImportResponse {
  success: boolean
  importedCount: number
  errors: string[]
  timecards: TimecardData[]
}

/**
 * Emmy Health Upload Row Result
 * Result for each row processed during Emmy Health upload
 */
export interface EmmyHealthUploadRowResult {
  row: number
  employeeUsername: string
  firstName: string
  lastName: string
  ssnLast4: string
  status: 'success' | 'failed' | 'skipped' | 'no_match'
  paystubId?: string
  wellnessAmount?: number
  claimAmount?: number
  error?: string
}

/**
 * Emmy Health Upload Result
 * Response from uploading Emmy Health CSV data for a batch
 */
export interface EmmyHealthUploadResult {
  batchId: string
  totalRows: number
  successCount: number
  failedCount: number
  skippedCount: number
  noMatchCount: number
  results: EmmyHealthUploadRowResult[]
}

/**
 * Payroll API Service
 * Handles all payroll-related API calls
 * Extends BaseApi for automatic Bearer token authorization
 */
export class PayrollApi extends BaseApi {
  // ==================== EOR ENDPOINTS ====================

  /* UPDATED REQUIREMENT (Jan 2025): Removed getEors() endpoint
     Use agencyApi.getAllEors() instead - the payroll/eors endpoint will be removed by BE
     
     OLD CODE (preserved for reference):
     async getEors(): Promise<EORListResponse> {
       try {
         const response = await this.client.get<EORListResponse>('/payroll/eors')
         return response.data
       } catch (error: unknown) {
         console.error('Error fetching EORs:', error)
         throw error
       }
     }
  */

  // ==================== CLIENT ENDPOINTS ====================

  /**
   * Get clients by EOR/Agency ID
   * GET /payroll/eors/:agencyId/clients
   *
   * @param agencyId - The agency/EOR ID
   */
  async getClientsByEOR(agencyId: string): Promise<ClientListResponse> {
    try {
      const response = await this.client.get<ClientListResponse>(
        `/payroll/eors/${agencyId}/clients`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching clients for EOR:', agencyId, error)
      throw error
    }
  }

  // ==================== BATCH ENDPOINTS ====================

  /**
   * Create a new payroll batch
   * POST /payroll/batches
   *
   * @param request - Batch creation parameters
   */
  async createBatch(request: CreateBatchRequest): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>('/payroll/batches', request)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating batch:', request, error)
      throw error
    }
  }

  /**
   * Get batch by ID
   * GET /payroll/batches/:batchId
   *
   * @param batchId - The batch ID
   */
  async getBatchById(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.get<BatchResponse>(`/payroll/batches/${batchId}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching batch:', batchId, error)
      throw error
    }
  }

  /**
   * Get all batches with optional filters
   * GET /payroll/batches?agencyId=xxx&companyId=xxx&status=xxx
   *
   * @param filters - Optional filter parameters
   */
  async getBatches(filters?: {
    agencyId?: string
    companyId?: string
    status?: BatchStatus
  }): Promise<BatchResponse[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.status) params.append('status', filters.status)

      const response = await this.client.get<BatchResponse[]>(
        `/payroll/batches${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching batches:', filters, error)
      throw error
    }
  }

  /**
   * Update an existing batch
   * PUT /payroll/batches/:batchId
   * UPDATED: Uses correct field names for mspplatform backend
   *
   * @param request - Batch update parameters
   */
  async updateBatch(request: UpdateBatchRequest): Promise<BatchResponse> {
    try {
      const {id, ...updateData} = request
      const response = await this.client.put<BatchResponse>(`/payroll/batches/${id}`, updateData)
      return response.data
    } catch (error: unknown) {
      console.error('Error updating batch:', request, error)
      throw error
    }
  }

  /**
   * Delete a batch (cleanup on invoice creation failure)
   * DELETE /payroll/batches/:batchId
   * Only works if batch has no invoice attached.
   *
   * @param batchId - The batch ID to delete
   */
  async deleteBatch(batchId: string): Promise<void> {
    try {
      await this.client.delete(`/payroll/batches/${batchId}`)
    } catch (error: unknown) {
      console.error('Error deleting batch:', batchId, error)
      throw error
    }
  }

  /**
   * Void a payroll batch and its invoice
   * POST /payroll/batches/:batchId/void
   * Marks both the batch and its associated invoice as voided.
   * Cannot void if batch is Finalized/Approved/Invoiced/Completed
   * or if invoice is Approved/Paid.
   *
   * @param batchId - The batch ID to void
   * @param reason - Optional reason for voiding
   */
  async voidBatch(batchId: string, reason?: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(
        `/payroll/batches/${batchId}/void`,
        reason ? {reason} : {},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error voiding batch:', batchId, error)
      throw error
    }
  }

  /**
   * Finalize a payroll batch
   * POST /payroll/batches/:batchId/finalize
   *
   * @param batchId - The batch ID to finalize
   */
  async finalizeBatch(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(`/payroll/batches/${batchId}/finalize`)
      return response.data
    } catch (error: unknown) {
      console.error('Error finalizing batch:', batchId, error)
      throw error
    }
  }

  /**
   * UPDATED REQUIREMENT (Jan 2025): Process payroll - send batch to payroll software
   * Called after customer approves invoice
   * Sends batch data to payroll provider microservice
   * Transitions batch status from "Approved" → "Closed"
   *
   * POST /payroll/batches/:batchId/process
   * TODO: Update endpoint path when backend implements this
   *
   * @param batchId - The batch ID to process
   */
  async processPayroll(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(`/payroll/batches/${batchId}/process`)
      return response.data
    } catch (error: unknown) {
      console.error('Error processing payroll for batch:', batchId, error)
      throw error
    }
  }

  /**
   * Send batch to payroll
   * Called after processPayroll to send the batch data to the external payroll system
   *
   * POST /payroll/batches/:batchId/send-to-payroll
   *
   * @param batchId - The batch ID to send to payroll
   */
  async sendToPayroll(batchId: string): Promise<SendToPayrollResponse> {
    try {
      const response = await this.client.post<SendToPayrollResponse>(
        `/payroll/batches/${batchId}/send-to-payroll`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error sending batch to payroll:', batchId, error)
      throw error
    }
  }

  // ==================== EARNINGS ENDPOINTS ====================

  /**
   * Create batch earnings records (calculate from timecards)
   * POST /payroll/earnings/batch
   *
   * @param request - Earnings creation parameters with timecards
   */
  async createBatchEarnings(
    request: CreateBatchEarningsRequest,
  ): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.post<EarningsRecordResponse[]>(
        '/payroll/earnings/batch',
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error creating batch earnings:', request, error)
      throw error
    }
  }

  /**
   * Get earnings records by batch ID
   * GET /payroll/earnings/batch/:batchId
   *
   * @param batchId - The batch ID
   */
  async getEarningsByBatchId(batchId: string): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.get<EarningsRecordResponse[]>(
        `/payroll/earnings/batch/${batchId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching earnings for batch:', batchId, error)
      throw error
    }
  }

  /**
   * Get earnings history for an employee/applicant
   * GET /payroll/earnings/applicant/:applicantId
   *
   * @param applicantId - The applicant/employee ID
   */
  async getEarningsByApplicant(applicantId: string): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.get<EarningsRecordResponse[]>(
        `/payroll/earnings/applicant/${applicantId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching earnings for applicant:', applicantId, error)
      throw error
    }
  }

  /**
   * Get specific earnings record (paystub)
   * GET /payroll/earnings/:earningsId
   *
   * @param earningsId - The earnings record ID
   */
  async getEarningsById(earningsId: string): Promise<EarningsRecordResponse> {
    try {
      const response = await this.client.get<EarningsRecordResponse>(
        `/payroll/earnings/${earningsId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching earnings record:', earningsId, error)
      throw error
    }
  }

  /**
   * Get employee YTD tax information
   * GET /payroll/earnings/ytd/:applicantId/:year
   *
   * @param applicantId - The applicant/employee ID
   * @param year - The tax year
   */
  async getYTDTaxInfo(applicantId: string, year: number): Promise<any> {
    try {
      const response = await this.client.get<any>(`/payroll/earnings/ytd/${applicantId}/${year}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching YTD tax info:', applicantId, year, error)
      throw error
    }
  }

  // ==================== INVOICE ENDPOINTS ====================

  /**
   * Generate invoice from batch with unprocessed timecards
   * POST /payroll/batches/:batchId/generate-invoice
   * UPDATED: New endpoint for auto-generating invoice from batch
   *
   * @param batchId - The batch ID
   */
  async generateInvoiceFromBatch(batchId: string): Promise<GenerateInvoiceResponse> {
    try {
      const response = await this.client.post<GenerateInvoiceResponse>(
        `/payroll/batches/${batchId}/generate-invoice`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error generating invoice from batch:', batchId, error)
      throw error
    }
  }

  /**
   * Create blank invoice for manual entry
   * POST /payroll/invoices/blank
   * UPDATED: New endpoint for creating empty invoices
   *
   * @param request - Blank invoice creation parameters
   */
  async createBlankInvoice(request: CreateBlankInvoiceRequest): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>('/payroll/invoices/blank', request)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating blank invoice:', request, error)
      throw error
    }
  }

  /**
   * Generate invoice from approved batch (LEGACY - kept for compatibility)
   * POST /payroll/invoices
   *
   * @param request - Invoice creation parameters
   */
  async generateInvoice(request: CreateInvoiceRequest): Promise<InvoiceWithItemsResponse> {
    try {
      const response = await this.client.post<InvoiceWithItemsResponse>(
        '/payroll/invoices',
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error generating invoice:', request, error)
      throw error
    }
  }

  /**
   * Get invoices with optional filters
   * GET /payroll/invoices?batchId=xxx&companyId=xxx&status=xxx&page=1&limit=20
   *
   * @param filters - Optional filter parameters
   */
  async getInvoices(filters?: {
    batchId?: string
    companyId?: string
    agencyId?: string
    status?: InvoiceStatus
    page?: number
    limit?: number
  }): Promise<{
    invoices: InvoiceResponse[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    try {
      const params = new URLSearchParams()
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())

      const response = await this.client.get<{
        invoices: InvoiceResponse[]
        total: number
        page: number
        limit: number
        totalPages: number
      }>(`/payroll/invoices${params.toString() ? `?${params.toString()}` : ''}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching invoices:', filters, error)
      throw error
    }
  }

  /**
   * Get invoice details with line items
   * GET /payroll/invoices/:invoiceId
   *
   * @param invoiceId - The invoice ID
   */
  async getInvoiceById(invoiceId: string): Promise<InvoiceWithItemsResponse> {
    try {
      const response = await this.client.get<InvoiceWithItemsResponse>(
        `/payroll/invoices/${invoiceId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching invoice:', invoiceId, error)
      throw error
    }
  }

  /**
   * Approve an invoice
   * POST /payroll/invoices/:invoiceId/approve
   *
   * @param invoiceId - The invoice ID to approve
   */
  async approveInvoice(invoiceId: string): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/approve`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error approving invoice:', invoiceId, error)
      throw error
    }
  }

  /**
   * Send invoice to customer
   * POST /payroll/invoices/:invoiceId/send
   * Updates invoice status to Sent with timestamp and user who sent it.
   * Only works on Pending invoices.
   *
   * @param invoiceId - The invoice ID to send
   */
  async sendInvoice(invoiceId: string): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/send`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error sending invoice:', invoiceId, error)
      throw error
    }
  }

  /**
   * Unsend invoice - revert to Pending
   * POST /payroll/invoices/:invoiceId/unsend
   * Reverts invoice status from Sent back to Pending.
   * Clears sent timestamp and user info.
   *
   * @param invoiceId - The invoice ID to unsend
   */
  async unsendInvoice(invoiceId: string): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/unsend`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error unsending invoice:', invoiceId, error)
      throw error
    }
  }

  /**
   * Download invoice PDF
   * GET /payroll/invoices/:invoiceId/pdf
   *
   * @param invoiceId - The invoice ID
   * @returns PDF blob
   */
  async downloadInvoicePDF(invoiceId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error downloading invoice PDF:', invoiceId, error)
      throw error
    }
  }

  // ==================== INVOICE ITEM CRUD ENDPOINTS ====================

  /**
   * Add invoice item (employee) to invoice
   * POST /payroll/invoices/:invoiceId/items
   * UPDATED: New endpoint for manually adding employees to invoices
   *
   * @param invoiceId - The invoice ID
   * @param request - Invoice item creation parameters
   */
  async addInvoiceItem(invoiceId: string, request: AddInvoiceItemRequest): Promise<InvoiceItem> {
    try {
      const response = await this.client.post<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error adding invoice item:', invoiceId, request, error)
      throw error
    }
  }

  /**
   * Update invoice item
   * PUT /payroll/invoices/:invoiceId/items/:itemId
   * UPDATED: New endpoint for updating employee earnings in invoice
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param request - Invoice item update parameters
   */
  async updateInvoiceItem(
    invoiceId: string,
    itemId: string,
    request: UpdateInvoiceItemRequest,
  ): Promise<InvoiceItem> {
    try {
      const response = await this.client.put<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items/${itemId}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating invoice item:', invoiceId, itemId, request, error)
      throw error
    }
  }

  /**
   * Delete invoice item (remove employee from invoice)
   * DELETE /payroll/invoices/:invoiceId/items/:itemId
   * UPDATED: New endpoint for removing employees from invoices
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   */
  async deleteInvoiceItem(invoiceId: string, itemId: string): Promise<void> {
    try {
      await this.client.delete(`/payroll/invoices/${invoiceId}/items/${itemId}`)
    } catch (error: unknown) {
      console.error('Error deleting invoice item:', invoiceId, itemId, error)
      throw error
    }
  }

  // ==================== EARNINGS LINE ENDPOINTS ====================

  /**
   * Update specific earnings line within an invoice item
   * PUT /payroll/invoices/:invoiceId/items/:itemId/earnings/:paycodeId
   * ADDED: New endpoint for updating individual earnings lines
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param paycodeId - The paycode ID of the earnings line to update
   * @param request - Partial earnings line update parameters (only provided fields are updated)
   */
  async updateEarningsLine(
    invoiceId: string,
    itemId: string,
    paycodeId: string,
    request: UpdateEarningsLineRequest,
  ): Promise<InvoiceItem> {
    try {
      const response = await this.client.put<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items/${itemId}/earnings/${paycodeId}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating earnings line:', invoiceId, itemId, paycodeId, request, error)
      throw error
    }
  }

  /**
   * Delete specific earnings line from an invoice item
   * DELETE /payroll/invoices/:invoiceId/items/:itemId/earnings/:paycodeId
   * ADDED: New endpoint for removing individual earnings lines
   * Note: If no earnings lines remain after deletion, the entire invoice item is deleted
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param paycodeId - The paycode ID of the earnings line to delete
   */
  async deleteEarningsLine(invoiceId: string, itemId: string, paycodeId: string): Promise<void> {
    try {
      await this.client.delete(
        `/payroll/invoices/${invoiceId}/items/${itemId}/earnings/${paycodeId}`,
      )
    } catch (error: unknown) {
      console.error('Error deleting earnings line:', invoiceId, itemId, paycodeId, error)
      throw error
    }
  }

  // ==================== TIMECARD ENDPOINTS ====================

  /**
   * Receive timecard data from Ronos webhook (Single or Batch)
   * POST /webhooks/ronos/timecards
   *
   * @param request - Ronos timecard data
   */
  async receiveRonosTimecards(request: any): Promise<any> {
    try {
      const response = await this.client.post<any>('/webhooks/ronos/timecards', request)
      return response.data
    } catch (error: unknown) {
      console.error('Error receiving Ronos timecards:', request, error)
      throw error
    }
  }

  /**
   * Get timecards with optional filters
   * GET /payroll/timecards
   *
   * @param filters - Optional filter parameters
   */
  async getTimecards(filters?: {
    batchId?: string
    applicantId?: string
    assignmentId?: string
    weekStartDate?: string
    weekEndDate?: string
    startDate?: string
    endDate?: string
    processed?: boolean
  }): Promise<TimecardResponse[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.applicantId) params.append('applicantId', filters.applicantId)
      if (filters?.assignmentId) params.append('assignmentId', filters.assignmentId)
      if (filters?.weekStartDate) params.append('weekStartDate', filters.weekStartDate)
      if (filters?.weekEndDate) params.append('weekEndDate', filters.weekEndDate)
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.processed !== undefined) params.append('processed', String(filters.processed))

      const response = await this.client.get<TimecardResponse[]>(
        `/payroll/timecards${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching timecards:', filters, error)
      throw error
    }
  }

  /**
   * Get timecards by assignment ID
   * GET /payroll/timecards/assignment/:assignmentId
   *
   * @param assignmentId - The assignment ID
   */
  async getTimecardsByAssignment(assignmentId: string): Promise<TimecardResponse[]> {
    try {
      const response = await this.client.get<TimecardResponse[]>(
        `/payroll/timecards/assignment/${assignmentId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching timecards for assignment:', assignmentId, error)
      throw error
    }
  }

  /**
   * Get all unprocessed timecards for batch creation
   * GET /payroll/timecards/unprocessed
   */
  async getUnprocessedTimecards(): Promise<TimecardData[]> {
    try {
      const response = await this.client.get<TimecardData[]>('/payroll/timecards/unprocessed')
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching unprocessed timecards:', error)
      throw error
    }
  }

  /**
   * Get paginated timecards with filters and sorting
   * GET /payroll/timecards/paginated
   *
   * @param params - Pagination, filter, and sort parameters
   * @returns Paginated timecard response with employee, site, department, and job title details
   */
  async getPaginatedTimecards(params: TimecardPagingParams): Promise<TimecardPagingResponse> {
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('pageNumber', params.pageNumber.toString())
      queryParams.append('pageSize', params.pageSize.toString())

      if (params.companyId) queryParams.append('companyId', params.companyId)
      if (params.siteId) queryParams.append('siteId', params.siteId)
      if (params.departmentId) queryParams.append('departmentId', params.departmentId)
      if (params.jobTitle) queryParams.append('jobTitle', params.jobTitle)
      if (params.employeeName) queryParams.append('employeeName', params.employeeName)
      if (params.weekStartDate) queryParams.append('weekStartDate', params.weekStartDate)
      if (params.weekEndDate) queryParams.append('weekEndDate', params.weekEndDate)
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

      const response = await this.client.get<TimecardPagingResponse>(
        `/payroll/timecards/paginated?${queryParams.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching paginated timecards:', error)
      throw error
    }
  }

  /**
   * Import timecard data from Toast POS system
   * POST /webhooks/toast/timecards
   *
   * @param request - Toast import parameters
   */
  async importToastTimecards(request: ToastImportRequest): Promise<ToastImportResponse> {
    try {
      const response = await this.client.post<ToastImportResponse>(
        '/webhooks/toast/timecards',
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error importing Toast timecards:', request, error)
      throw error
    }
  }

  /**
   * Upload CSV file for timecard import
   * POST /webhooks/toast/timecards (with FormData)
   *
   * @param file - CSV file containing timecard data
   * @param clientId - The client/company ID
   * @param eorId - The EOR/agency ID
   */
  async uploadTimecardCSV(
    file: File,
    clientId: string,
    eorId: string,
  ): Promise<ToastImportResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clientId', clientId)
      formData.append('eorId', eorId)

      const response = await this.client.post<ToastImportResponse>(
        '/webhooks/toast/timecards',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error uploading timecard CSV:', error)
      throw error
    }
  }

  // ==================== BATCH REPORT ENDPOINTS ====================

  /**
   * Generate batch report
   * POST /payroll/batches/:batchId/reports/generate
   *
   * @param batchId - The batch ID
   */
  async generateBatchReport(batchId: string): Promise<any> {
    try {
      const response = await this.client.post<any>(`/payroll/batches/${batchId}/reports/generate`)
      return response.data
    } catch (error: unknown) {
      console.error('Error generating batch report:', batchId, error)
      throw error
    }
  }

  /**
   * Get batch report metadata
   * GET /payroll/batches/:batchId/reports
   *
   * @param batchId - The batch ID
   */
  async getBatchReportMetadata(batchId: string): Promise<any> {
    try {
      const response = await this.client.get<any>(`/payroll/batches/${batchId}/reports`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching batch report metadata:', batchId, error)
      throw error
    }
  }

  /**
   * Download batch report
   * GET /payroll/batches/:batchId/reports/download
   *
   * @param batchId - The batch ID
   * @returns Report file blob
   */
  async downloadBatchReport(batchId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/batches/${batchId}/reports/download`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error downloading batch report:', batchId, error)
      throw error
    }
  }

  /**
   * Download Emmy Health Report for a batch
   * GET /payroll/reports/emmy-health/batch/:batchId
   *
   * Generates and downloads an Emmy Health CSV report containing employee
   * demographics, tax withholding information, and wage data for all paystubs
   * in the specified batch.
   *
   * @param batchId - The batch ID
   * @returns CSV file blob
   */
  async downloadEmmyHealthReport(batchId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/reports/emmy-health/batch/${batchId}`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error downloading Emmy Health report:', batchId, error)
      throw error
    }
  }

  /**
   * Upload Emmy Health Report for a batch
   * POST /payroll/reports/emmy-health/batch/:batchId/upload
   *
   * Uploads an Emmy Health CSV containing per-pay-period premium and claim amounts.
   * The backend will:
   * - Match employees by EmployeeUsername or SSN last 4 digits
   * - Apply Wellness (pre-tax deduction) amounts
   * - Apply Claim (post-tax surplus to NET pay) amounts
   * - Recalculate taxes for affected paystubs
   *
   * NOTE: Upload is only allowed when batch status is "Invoiced".
   *
   * @param batchId - The batch ID
   * @param file - CSV file from Emmy Health
   * @returns Upload result with success/failure counts
   */
  async uploadEmmyHealthReport(batchId: string, file: File): Promise<EmmyHealthUploadResult> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await this.client.post<EmmyHealthUploadResult>(
        `/payroll/reports/emmy-health/batch/${batchId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error uploading Emmy Health report:', batchId, error)
      throw error
    }
  }
}

// Export a singleton instance
export const payrollApi = new PayrollApi()

export default payrollApi
